import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'

import type { TimeSuggestion } from '../types'

/**
 * Props for the useSuggestionSync hook
 */
type UseSuggestionSyncProps = {
  /** Whether the suggestion dropdown is currently open */
  isOpen: boolean
  /** The currently highlighted suggestion index (-1 if none) */
  activeIndex: number
  /** The index of the suggestion that matches the current input value (-1 if no match) */
  matchIndex: number
  /** Whether the user has manually navigated through suggestions using keyboard */
  userNavigated: boolean
  /** Array of available time suggestions */
  suggestions: TimeSuggestion[]
  /** Reference to the suggestion list container DOM element */
  listRef: RefObject<HTMLDivElement>
  /** Callback to auto-highlight a suggestion at the given index */
  onAutoHighlight: (index: number) => void
  /** Callback to update the active index, with flag indicating if user-initiated */
  onSetActiveIndex: (index: number, userInitiated: boolean) => void
  /** Whether the active item should be scrolled into view */
  shouldScrollToActive: boolean
  /** The current input value (used to determine if auto-highlight should occur) */
  inputValue: string
}

/**
 * Manages suggestion highlighting and synchronization for SmartTimeInput
 *
 * This hook orchestrates the complex interaction between user navigation,
 * automatic highlighting, and visual scrolling of time suggestions. It ensures
 * that the UI remains consistent and responsive while respecting user intent.
 *
 * Key behaviors:
 * - **Auto-scroll**: Keeps the highlighted suggestion visible by scrolling it into view
 * - **Auto-highlight**: Automatically highlights suggestions that match the current input,
 *   but respects user navigation to avoid disrupting their flow
 * - **State synchronization**: Keeps activeIndex valid when suggestions change
 * - **Bounds checking**: Prevents activeIndex from exceeding array bounds
 * - **User intent preservation**: Tracks whether the user has manually navigated to prevent
 *   auto-highlight from overriding their selection
 *
 * @param props - Configuration object for suggestion synchronization
 * @param props.isOpen - Whether the suggestion dropdown is currently open
 * @param props.activeIndex - The currently highlighted suggestion index (-1 if none)
 * @param props.matchIndex - The index of the suggestion that matches the current input value (-1 if no match)
 * @param props.userNavigated - Whether the user has manually navigated through suggestions using keyboard
 * @param props.suggestions - Array of available time suggestions
 * @param props.listRef - Reference to the suggestion list container DOM element
 * @param props.onAutoHighlight - Callback to auto-highlight a suggestion at the given index
 * @param props.onSetActiveIndex - Callback to update the active index, with flag indicating if user-initiated
 *
 * @example
 * ```tsx
 * useSuggestionSync({
 *   isOpen: menuOpen,
 *   activeIndex: highlightedIndex,
 *   matchIndex: currentMatchIndex,
 *   userNavigated: hasUserNavigated,
 *   suggestions: timeSuggestions,
 *   listRef: suggestionListRef,
 *   onAutoHighlight: handleAutoHighlight,
 *   onSetActiveIndex: setActiveIndex,
 * })
 * ```
 */
export const useSuggestionSync = ({
  isOpen,
  activeIndex,
  matchIndex,
  userNavigated,
  suggestions,
  listRef,
  onAutoHighlight,
  onSetActiveIndex,
  shouldScrollToActive,
  inputValue,
}: UseSuggestionSyncProps): void => {
  // Track if user has explicitly set an index in this menu session
  // This prevents auto-highlight from overriding user navigation during state transitions
  const hasExplicitIndexRef = useRef(false)

  // Reset the explicit index flag when menu closes
  useEffect(() => {
    if (!isOpen) {
      hasExplicitIndexRef.current = false
    }
  }, [isOpen])

  // Track when user explicitly navigates
  useEffect(() => {
    if (isOpen && activeIndex >= 0 && userNavigated) {
      hasExplicitIndexRef.current = true
    }
  }, [isOpen, activeIndex, userNavigated])

  // Auto-scroll active item into view (only when shouldScrollToActive is true)
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current && shouldScrollToActive) {
      const el = listRef.current.children[activeIndex]
      if (el instanceof HTMLElement) {
        el.scrollIntoView?.({
          block: 'center',
          behavior: 'instant',
        })
      }
    }
  }, [activeIndex, listRef, shouldScrollToActive])

  // Auto-highlight on matchIndex change (only when there's user input)
  useEffect(() => {
    if (!isOpen || userNavigated) {
      return
    }
    // Don't override if user has explicitly set an index in this session
    if (hasExplicitIndexRef.current && activeIndex >= 0) {
      return
    }
    // Only auto-highlight when there's actual user input (not when menu opens with empty input)
    if (inputValue.trim() === '') {
      return
    }
    if (matchIndex >= 0 && matchIndex !== activeIndex) {
      onAutoHighlight(matchIndex)
    }
  }, [
    isOpen,
    matchIndex,
    userNavigated,
    activeIndex,
    onAutoHighlight,
    inputValue,
  ])

  // Bounds checking for activeIndex
  useEffect(() => {
    if (!isOpen) {
      return
    }
    if (suggestions.length === 0 && activeIndex !== -1) {
      onSetActiveIndex(-1, false)
      return
    }
    if (activeIndex >= suggestions.length && suggestions.length > 0) {
      onSetActiveIndex(suggestions.length - 1, userNavigated)
    }
  }, [activeIndex, isOpen, suggestions.length, userNavigated, onSetActiveIndex])
}
